import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useState } from 'react';
import {
  FileCheck2, AlertTriangle, Loader2, Download, HelpCircle, Ban, FileText,
  Receipt,
} from 'lucide-react';
import { api } from '../api';
import { guardarArchivo } from '../ui/descarga';
import { useAvisos } from '../lib/contextoAvisos';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import TiempoRelativo from '../ui/TiempoRelativo';

// LAS FACTURAS EMITIDAS.
//
// LO QUE ESTA PANTALLA EXISTE PARA RESOLVER es el estado EN DUDA. Todo lo
// demás —una lista de folios— se podría mirar en cualquier informe; una
// factura de la que no se sabe si se emitió necesita a alguien que pulse
// un botón, y necesita que ese botón NO SEA «volver a timbrar».
//
// Por eso el aviso de las dudosas va arriba del todo y en amarillo, y por
// eso el botón dice «Confirmar con el SAT» y no «Reintentar»: reintentar
// es exactamente lo que produciría el segundo timbre.

const TONO = {
  TIMBRADA: 'text-green-700',
  EN_DUDA: 'text-amber-700',
  FALLIDA: 'text-red-700',
  TIMBRANDO: 'text-gray-500',
  CANCELADA: 'text-gray-500',
};

const NOMBRE = {
  TIMBRADA: 'Timbrada',
  EN_DUDA: 'Sin confirmar',
  FALLIDA: 'No se emitió',
  TIMBRANDO: 'Emitiendo…',
  CANCELADA: 'Cancelada',
};

export default function Facturas() {
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState('');
  // LOS ABONOS QUE EL SAT TODAVÍA ESPERA. Se cargan siempre, aunque se
  // esté mirando la lista de facturas: si hay alguno, es lo que hay que
  // resolver, y esconderlo detrás de una pestaña sería esconderlo.
  const [pendientes, setPendientes] = useState([]);
  // LOS MESES DE MOSTRADOR. Igual que los abonos: se cargan siempre,
  // porque un mes cerrado sin declarar es lo que hay que resolver.
  const [globales, setGlobales] = useState([]);
  const [vista, setVista] = useState('facturas');
  useSubpantalla('vista', vista !== 'facturas', () => setVista('facturas'), { valor: vista, abrir: setVista, enUrl: true, reemplaza: true });
  const avisos = useAvisos();

  const cargar = useCallback(
    () => Promise.all([
      api.get('/facturas').then(setFilas),
      api.get('/complementos-pago/pendientes').then(setPendientes)
        .catch(() => setPendientes([])),
      api.get('/facturas-globales/pendientes').then(setGlobales)
        .catch(() => setGlobales([])),
    ]).catch((e) => setError(e.message)),
    [],
  );
  useEffect(() => { cargar(); }, [cargar]);

  if (error) {
    return (
      <div className="m-4 flex gap-2 rounded-xl border border-red-200 bg-red-50
                      p-3 text-sm text-red-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{error}</span>
      </div>
    );
  }
  if (!filas) {
    return (
      <p className="flex items-center gap-2 p-4 text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Cargando tus facturas…
      </p>
    );
  }

  const dudosas = filas.filter((f) => f.estado === 'EN_DUDA');
  // Un mes SIN DECLARAR es el que ya cerró, tiene tickets y no tiene su
  // global. El mes en curso no cuenta: todavía entran ventas.
  const sinDeclarar = globales.filter(
    (g) => !g.en_curso && !g.ya_emitida && g.tickets > 0);
  const globalesPendientes = sinDeclarar.length;

  return (
    <div className="flex flex-col gap-4 p-4 max-w-4xl">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Facturas</h1>
        <p className="text-gray-600">
          Los comprobantes que has emitido, con su folio fiscal.
        </p>
      </header>

      {/* LO PRIMERO QUE SE VE, si lo hay. Una factura sin confirmar no
          puede quedarse en medio de una lista larga. */}
      {dudosas.length > 0 && (
        <NotaExplicativa titulo={`${dudosas.length} factura(s) sin confirmar`}>
          Se cortó la conexión con el servicio de timbrado y no sabemos si
          llegaron a emitirse. <strong>No las vuelvas a timbrar</strong>: eso
          podría emitirlas dos veces. Pulsa <strong>Confirmar con el SAT</strong>
          {' '}en cada una y te decimos qué pasó.
        </NotaExplicativa>
      )}

      {/* UN ABONO SIN SU COMPLEMENTO ES DINERO QUE, PARA EL SAT, NO SE HA
          COBRADO. Va arriba y en amarillo por lo mismo que las dudosas:
          necesita a alguien que pulse un botón. */}
      {pendientes.length > 0 && (
        <NotaExplicativa
          titulo={`${pendientes.length} abono(s) esperan su complemento de pago`}>
          Cuando te pagan una venta a crédito, el SAT pide un comprobante
          aparte por cada abono. Estos no se pudieron emitir y{' '}
          <strong>el cobro sí está registrado</strong>: solo falta el
          trámite.{' '}
          <button type="button" onClick={() => setVista('complementos')}
                  className="font-semibold underline">
            Verlos y emitirlos
          </button>
        </NotaExplicativa>
      )}

      {/* UN MES CERRADO SIN DECLARAR es dinero que entró y que para
          Hacienda no existe. Va arriba, como las otras dos alarmas. */}
      {sinDeclarar.length > 0 && (
        <NotaExplicativa
          titulo={`${sinDeclarar.length} mes(es) de mostrador sin declarar`}>
          Las ventas a quien no pide factura se declaran juntas, una vez al
          mes. Estos meses ya cerraron y no tienen la suya.{' '}
          <button type="button" onClick={() => setVista('global')}
                  className="font-semibold underline">
            Verlos y emitirlas
          </button>
        </NotaExplicativa>
      )}

      <div className="flex flex-wrap gap-2 border-b border-gray-200">
        {[['facturas', 'Facturas', 0],
          ['complementos', 'Complementos de pago', pendientes.length],
          ['global', 'Global del mes', globalesPendientes]]
          .map(([id, texto, cuantos]) => (
            <button
              key={id} type="button" onClick={() => setVista(id)} aria-current={vista === id ? 'page' : undefined}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${
                vista === id
                  ? 'border-[var(--color-marca)] text-[var(--color-marca)]'
                  : 'border-transparent text-gray-600'}`}>
              {texto}
              {id !== 'facturas' && cuantos > 0 && (
                <span className="ml-1 rounded-full bg-amber-100 px-1.5
                                 text-[11px] text-amber-800">{cuantos}</span>
              )}
            </button>
          ))}
      </div>

      {vista === 'complementos' ? (
        <Complementos pendientes={pendientes} onCambio={cargar}
                      onAviso={avisos.exito} onError={setError} />
      ) : vista === 'global' ? (
        <Global meses={globales} onCambio={cargar}
                onAviso={avisos.exito} onError={setError} />
      ) : (filas || []).length === 0 ? (
        <EstadoVacio
          icono={FileCheck2}
          titulo="Todavía no has emitido ninguna factura"
          descripcion="Se factura desde una venta: búscala en Ventas y pulsa «Facturar». Antes hace falta tener tus datos fiscales y tu Sello Digital cargados en «Preparar la facturación»."
        />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
          {(filas || []).map((f) => (
            <Renglon key={f.id} f={f} onCambio={cargar}
                     onAviso={avisos.exito} onError={setError} />
          ))}
        </ul>
      )}
    </div>
  );
}

// ------------------------------------------- los complementos de pago
// QUÉ SON, DICHO SIN JERGA. Nadie sabe qué es un «REP» ni tiene por qué
// saberlo: es el papel que dice que te pagaron un abono de algo que
// vendiste a crédito. Si no se emite, para Hacienda esa venta sigue sin
// cobrarse.
function Complementos({ pendientes, onCambio, onAviso, onError }) {
  const [emitidos, setEmitidos] = useState(null);

  useEffect(() => {
    api.get('/complementos-pago').then(setEmitidos).catch(() => setEmitidos([]));
  }, [pendientes]);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-600">
        Cuando vendes a crédito, la factura sale antes de cobrar. Por cada
        abono que te pagan después, el SAT pide este comprobante aparte.
        TEGO lo emite solo al registrar el abono; aquí solo aparece lo que
        haya quedado pendiente.
      </p>

      {pendientes.length > 0 && (
        <ul className="divide-y divide-amber-100 rounded-xl border
                       border-amber-200 bg-amber-50">
          {pendientes.map((p) => (
            <Pendiente key={p.id} p={p} onCambio={onCambio}
                       onAviso={onAviso} onError={onError} />
          ))}
        </ul>
      )}

      {emitidos === null ? (
        <p className="flex items-center gap-2 text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Cargando…
        </p>
      ) : emitidos.length === 0 && pendientes.length === 0 ? (
        <EstadoVacio
          icono={Receipt}
          titulo="Todavía no hay ningún complemento de pago"
          descripcion="Aparecerán solos en cuanto registres el primer abono de una venta a crédito que ya esté facturada. No hay que emitirlos a mano."
        />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
          {emitidos.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-x-3
                                      px-3 py-2 text-sm">
              <span className="font-mono font-semibold text-gray-900">
                {c.folio_completo}
              </span>
              <span className={TONO[c.estado]}>{NOMBRE[c.estado]}</span>
              <span className="text-gray-900">{c.nombre_receptor}</span>
              {c.factura && (
                <span className="text-gray-600">
                  Pago {c.num_parcialidad} de la factura {c.factura}
                </span>
              )}
              {c.modo === 'PRUEBAS' && (
                <span className="rounded bg-amber-100 px-1.5 py-0.5
                                 text-[11px] font-semibold text-amber-800">
                  práctica · sin valor fiscal
                </span>
              )}
              <span className="ml-auto text-gray-500">
                <TiempoRelativo valor={c.creado_en} />
              </span>
              {c.imp_pagado && (
                <span className="w-full text-gray-600">
                  Abonó {moneda(c.imp_pagado)} · quedan{' '}
                  {moneda(c.imp_saldo_insoluto)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Pendiente({ p, onCambio, onAviso, onError }) {
  const [ocupado, setOcupado] = useState(false);
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2
                   text-sm">
      <span className="text-gray-900">{p.cliente}</span>
      <span className="font-semibold text-gray-900">{moneda(p.importe)}</span>
      <span className="text-gray-600">de la factura {p.factura}</span>
      <span className="text-gray-500">
        <TiempoRelativo valor={p.creado_en} />
      </span>
      <button
        type="button" disabled={ocupado}
        onClick={async () => {
          setOcupado(true);
          try {
            const r = await api.post('/complementos-pago', { id_cobro: p.id });
            onAviso(r.mensaje);
            onCambio();
          } catch (e) { onError(e.message); } finally { setOcupado(false); }
        }}
        className="ml-auto inline-flex items-center gap-1 rounded-lg
                   bg-amber-600 px-3 py-2 font-semibold text-white
                   disabled:bg-gray-300">
        {ocupado
          ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          : <Receipt className="h-4 w-4" aria-hidden="true" />}
        Emitir su complemento
      </button>
    </li>
  );
}

// ------------------------------------------------- la global del mes
// QUÉ ES, DICHO SIN JERGA. Nadie llama a esto «CFDI global». Es «lo que
// vendiste a quien no pidió factura», y se declara junto una vez al mes.
function Global({ meses, onCambio, onAviso, onError }) {
  if (meses.length === 0) {
    return (
      <EstadoVacio
        icono={Receipt}
        titulo="Todavía no hay ventas de mostrador que declarar"
        descripcion="Aquí aparecerá cada mes con las ventas a quien no pidió factura, para emitirlas juntas cuando el mes cierre. Si todas tus ventas llevan cliente, esta lista se queda vacía y no pasa nada."
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-600">
        Las ventas a quien no pide factura no se quedan fuera de tu
        contabilidad: el SAT pide que al cerrar cada mes se declaren todas
        juntas en un solo comprobante. Aquí está mes por mes.
      </p>
      <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
        {meses.map((m) => (
          <MesGlobal key={`${m.periodo}-${m.id_sucursal}`} m={m}
                     onCambio={onCambio} onAviso={onAviso} onError={onError} />
        ))}
      </ul>
    </div>
  );
}

const MES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
  'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

function enLetra(periodo) {
  const [anio, mes] = periodo.split('-');
  return `${MES[Number(mes) - 1]} de ${anio}`;
}

function MesGlobal({ m, onCambio, onAviso, onError }) {
  const [ocupado, setOcupado] = useState(false);
  // Botón apagado CON LA RAZÓN A LA VISTA, nunca apagado a secas.
  const razon = m.en_curso
    ? 'Este mes todavía no termina: aún pueden entrar ventas.'
    : m.ya_emitida
      ? `Ya declarado con el folio ${m.folio}.`
      : m.tickets === 0
        ? 'No hubo ventas de mostrador este mes.'
        : '';

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2
                   text-sm">
      <span className="font-semibold text-gray-900">
        {enLetra(m.periodo)}
      </span>
      {m.sucursal && <span className="text-gray-500">{m.sucursal}</span>}
      <span className="text-gray-600">
        {m.tickets} ticket(s) · {moneda(m.total)}
      </span>
      {m.ya_emitida && (
        <span className="text-green-700">Declarado · {m.folio}</span>
      )}
      {m.muchos && !m.ya_emitida && (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px]
                         font-semibold text-amber-800">
          son muchos: tardará un poco
        </span>
      )}
      <span className="ml-auto flex items-center gap-3">
        {razon && <span className="text-gray-500">{razon}</span>}
        {!razon && (
          <button
            type="button" disabled={ocupado}
            onClick={async () => {
              setOcupado(true);
              try {
                const r = await api.post('/facturas-globales', {
                  periodo: m.periodo, id_sucursal: m.id_sucursal,
                });
                onAviso(r.mensaje);
                onCambio();
              } catch (e) { onError(e.message); } finally { setOcupado(false); }
            }}
            className="inline-flex items-center gap-1 rounded-lg
                       bg-[var(--color-marca)] px-3 py-2 font-semibold
                       text-white disabled:bg-gray-300">
            {ocupado
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              : <Receipt className="h-4 w-4" aria-hidden="true" />}
            Declarar este mes
          </button>
        )}
      </span>
    </li>
  );
}

function moneda(v) {
  return Number(v).toLocaleString('es-MX',
    { style: 'currency', currency: 'MXN' });
}

function Renglon({ f, onCambio, onAviso, onError }) {
  const [ocupado, setOcupado] = useState(false);
  const [cancelando, setCancelando] = useState(false);

  return (
    <li className="flex flex-col gap-1 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-3">
        <span className="font-mono font-semibold text-gray-900">
          {f.folio_completo}
        </span>
        <span className="text-gray-900">{f.nombre_receptor}</span>
        <span className="text-gray-500">{f.rfc_receptor}</span>
        <span className={TONO[f.estado]}>{NOMBRE[f.estado]}</span>
        {/* UNA FACTURA DE PRÁCTICA NO SE PUEDE CONFUNDIR CON UNA DE
            VERDAD, ni de un vistazo ni en la lista. */}
        {f.modo === 'PRUEBAS' && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px]
                           font-semibold text-amber-800">
            práctica · sin valor fiscal
          </span>
        )}
        <span className="ml-auto text-gray-500">
          <TiempoRelativo valor={f.creado_en} />
        </span>
      </div>

      {f.uuid_cfdi && (
        <span className="font-mono text-[11px] text-gray-500">
          Folio fiscal {f.uuid_cfdi}
        </span>
      )}
      {f.error_mensaje && f.estado !== 'EN_DUDA' && (
        <span className="text-red-700">{f.error_mensaje}</span>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {f.estado === 'EN_DUDA' && (
          <button
            type="button" disabled={ocupado}
            onClick={async () => {
              setOcupado(true);
              try {
                const r = await api.post(`/facturas/${f.id}/confirmar`, {});
                onAviso(r.mensaje);
                onCambio();
              } catch (e) { onError(e.message); } finally { setOcupado(false); }
            }}
            className="inline-flex items-center gap-1 rounded-lg bg-amber-600
                       px-3 py-2 font-semibold text-white disabled:bg-gray-300">
            {ocupado
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              : <HelpCircle className="h-4 w-4" aria-hidden="true" />}
            Confirmar con el SAT
          </button>
        )}
        {(f.estado === 'TIMBRADA' || f.estado === 'CANCELADA') && (
          <>
            {/* EL PDF PRIMERO: es lo que se le da al cliente. El XML es el
                documento legal, pero nadie le entrega un XML a nadie. */}
            <button
              type="button"
              onClick={async () => {
                try {
                  guardarArchivo(await api.bajar(`/facturas/${f.id}/pdf`),
                                 `${f.folio_completo}.pdf`);
                } catch (e) { onError(e.message); }
              }}
              className="inline-flex items-center gap-1 font-semibold
                         text-[var(--color-marca)]">
              <FileText className="h-4 w-4" aria-hidden="true" />
              Descargar el PDF
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  guardarArchivo(await api.bajar(`/facturas/${f.id}/xml`),
                                 `${f.folio_completo}.xml`);
                } catch (e) { onError(e.message); }
              }}
              className="inline-flex items-center gap-1 text-gray-600">
              <Download className="h-4 w-4" aria-hidden="true" />
              XML
            </button>
          </>
        )}
        {f.estado === 'TIMBRADA' && (
          <button
            type="button" onClick={() => setCancelando(true)}
            className="inline-flex items-center gap-1 text-red-700">
            <Ban className="h-4 w-4" aria-hidden="true" />
            Cancelar
          </button>
        )}
        <span className="text-gray-500">
          {Number(f.total).toLocaleString('es-MX',
            { style: 'currency', currency: 'MXN' })}
        </span>
        {f.quien && <span className="text-gray-400">· {f.quien}</span>}
      </div>

      {cancelando && (
        <DialogoCancelar
          f={f} onCerrar={() => setCancelando(false)}
          onListo={(msg) => { setCancelando(false); onAviso(msg); onCambio(); }}
          onError={onError} />
      )}
    </li>
  );
}

// ------------------------------------------------------- cancelación
// LOS MOTIVOS DEL SAT, DICHOS COMO LOS DIRÍA UNA PERSONA. «Comprobante
// emitido con errores con relación» no se le pone a nadie en un menú.
//
// Y el motivo 01 pide la factura que sustituye a esta ANTES de dejar
// pulsar: es el error más común de todos, y el SAT lo rechaza con un
// código que no lo dice.
function DialogoCancelar({ f, onCerrar, onListo, onError }) {
  const [motivos, setMotivos] = useState([]);
  const [motivo, setMotivo] = useState('');
  const [sustituta, setSustituta] = useState('');
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api.get('/facturas/motivos-cancelacion').then(setMotivos).catch(() => {});
  }, []);

  const elegido = motivos.find((m) => m.clave === motivo);
  const faltaSustituta = elegido?.exige_sustituta
    && !/^[0-9A-Fa-f-]{36}$/.test(sustituta.trim());
  const listo = motivo && !faltaSustituta;

  return (
    <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3">
      <p className="font-semibold text-red-900">
        Cancelar la factura {f.folio_completo}
      </p>
      <p className="text-sm text-red-800">
        Se le pide al SAT, no se borra aquí. Según el motivo, tu cliente
        tendrá que aceptarlo.
      </p>

      <div className="mt-2 flex flex-col gap-1">
        {motivos.map((m) => (
          <label key={m.clave} className="flex items-start gap-2 text-sm">
            <input type="radio" name={`motivo-${f.id}`} value={m.clave}
                   checked={motivo === m.clave}
                   onChange={() => setMotivo(m.clave)} className="mt-1" />
            <span>
              <span className="font-medium text-gray-900">{m.texto}</span>
              <span className="block text-xs text-gray-600">{m.ayuda}</span>
            </span>
          </label>
        ))}
      </div>

      {elegido?.exige_sustituta && (
        <label className="mt-2 block text-sm">
          <span className="font-semibold text-gray-700">
            Folio fiscal de la factura que la sustituye
          </span>
          <input value={sustituta} onChange={(e) => setSustituta(e.target.value)}
                 placeholder="ABCD1234-..."
                 className="mt-1 w-full rounded-lg border border-gray-300
                            px-3 py-2 font-mono text-xs" />
          <span className="mt-1 block text-xs text-gray-600">
            Cópialo de la factura buena, que ya tiene que estar emitida.
          </span>
        </label>
      )}

      <label className="mt-2 block text-sm">
        <span className="font-semibold text-gray-700">
          Nota (para acordarte dentro de dos años)
        </span>
        <input value={nota} onChange={(e) => setNota(e.target.value)}
               className="mt-1 w-full rounded-lg border border-gray-300
                          px-3 py-2" />
      </label>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={onCerrar} className="px-3 py-2
                text-gray-600 font-semibold">
          Mejor no
        </button>
        <button
          type="button" disabled={!listo || enviando}
          onClick={async () => {
            setEnviando(true);
            try {
              const r = await api.post(`/facturas/${f.id}/cancelar`, {
                motivo,
                uuid_sustituto: sustituta.trim().toUpperCase() || null,
                nota: nota || null,
              });
              onListo(r.mensaje);
            } catch (e) { onError(e.message); } finally { setEnviando(false); }
          }}
          className="inline-flex items-center gap-2 rounded-xl bg-red-600
                     px-4 py-2 font-semibold text-white disabled:bg-gray-300">
          {enviando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Pedir la cancelación
        </button>
        {/* Botón apagado CON LA RAZÓN A LA VISTA. */}
        {!motivo && (
          <span className="text-sm text-gray-600">Elige primero un motivo.</span>
        )}
        {faltaSustituta && (
          <span className="text-sm text-red-700">
            Ese motivo necesita el folio fiscal de la factura que la
            sustituye.
          </span>
        )}
      </div>
    </div>
  );
}

